/**
 * operatorIntelligence.ts — MER Algo Footprints v8.6
 *
 * Deterministic Operator Intelligence Layer.
 *
 * Consumes existing intelligence stack outputs and produces:
 *   - SessionScore             — overall session quality score (0–100) + grade
 *   - DecisionGrade            — per-decision quality / timing / calibration
 *   - PatternInsight           — recurring structured signal/context patterns
 *   - OperatorGuidancePackage  — concise evidence-backed guidance
 *   - SessionIntelligenceSummary — complete operator-facing digest
 *
 * ── Design rules ──────────────────────────────────────────────────────────────
 * 1. Pure function — same inputs always produce same outputs
 * 2. Deterministic — no Date.now() in semantic output
 * 3. Evidence-backed — every item traces to a named rule + reason
 * 4. Structured-first — no reasoning-string parsing for core logic
 * 5. Conservative — does not overclaim causality or patterns
 * 6. Thresholds centralized — OPERATOR_INTELLIGENCE_THRESHOLDS only
 *
 * ── Input sources ─────────────────────────────────────────────────────────────
 *   OutcomeValidationResult[]  — from evaluateSession()
 *   SessionAttributionSummary  — from buildSessionAttributionSummary()
 *   ReplayIntelligenceOutput   — from analyseSnapshots()
 *   AdaptiveLearningProfile    — from buildAdaptiveLearningProfile() (optional)
 */

import type {
  OutcomeValidationResult,
  SessionAttributionSummary,
  PrimaryAttributionLayer,
} from '../replay/outcomeValidation';
import type {
  ReplayIntelligenceOutput,
  TurningPoint,
} from '../replay/replayIntelligence';
import type { AdaptiveLearningProfile } from '../learning/adaptiveLearning';

// ── Centralized thresholds ────────────────────────────────────────────────────

export const OPERATOR_INTELLIGENCE_THRESHOLDS = {
  // Session score component weights (must sum to 1.0)
  SCORE_WEIGHT_WIN_RATE:          0.30,
  SCORE_WEIGHT_CALIBRATION:       0.20,
  SCORE_WEIGHT_CONVICTION:        0.15,
  SCORE_WEIGHT_SIGNAL_CLARITY:    0.15,
  SCORE_WEIGHT_TRAP_QUALITY:      0.10,
  SCORE_WEIGHT_REPLAY_QUALITY:    0.10,

  // Grade boundaries
  GRADE_A_MIN:    80,
  GRADE_B_MIN:    65,
  GRADE_C_MIN:    50,
  GRADE_D_MIN:    35,

  // Decision quality thresholds
  HIGH_QUALITY_WIN_RATE:   0.65,
  LOW_QUALITY_WIN_RATE:    0.40,
  HIGH_CONVICTION_MIN:     70,
  LOW_CONVICTION_MAX:      45,

  // Timing: snapshot is 'on-time' if within ±this fraction of the event window
  // We use turning-point proximity: if there is a turning point near the snapshot
  // (within TIMING_WINDOW_SNAPSHOTS entries), consider it 'on_time'
  TIMING_WINDOW_SNAPSHOTS: 2,

  // Pattern detection
  PATTERN_MIN_OCCURRENCES:  2,

  // Calibration rate thresholds for guidance
  OVERCONF_RATE_GUIDANCE:   0.40,
  UNDERCONF_RATE_GUIDANCE:  0.40,

  // Minimum evaluations for meaningful scoring
  MIN_EVALS_FOR_SCORE:      2,

  // Trap quality: fraction of trap-warned decisions that were losses = poor trap quality
  TRAP_QUALITY_BAD_LOSS_RATE: 0.60,
} as const;

const T = OPERATOR_INTELLIGENCE_THRESHOLDS;

// Verify weights sum to 1.0 at module load
const _weightSum = T.SCORE_WEIGHT_WIN_RATE + T.SCORE_WEIGHT_CALIBRATION +
  T.SCORE_WEIGHT_CONVICTION + T.SCORE_WEIGHT_SIGNAL_CLARITY +
  T.SCORE_WEIGHT_TRAP_QUALITY + T.SCORE_WEIGHT_REPLAY_QUALITY;
if (Math.abs(_weightSum - 1.0) > 0.001) {
  throw new Error(`OPERATOR_INTELLIGENCE_THRESHOLDS: weights sum to ${_weightSum}, must equal 1.0`);
}

// ── Output types ──────────────────────────────────────────────────────────────

export type SessionGrade   = 'A' | 'B' | 'C' | 'D' | 'F' | 'insufficient_data';
export type DecisionQuality = 'high' | 'medium' | 'low' | 'unknown';
export type DecisionTiming  = 'early' | 'on_time' | 'late' | 'unknown';

export interface SessionScore {
  overall:    number;
  grade:      SessionGrade;
  components: {
    winRate:       number | null;
    calibration:   number | null;
    conviction:    number | null;
    signalClarity: number | null;
    trapQuality:   number | null;
    replayQuality: number | null;
  };
  reasoning: string[];
}

export interface DecisionGrade {
  snapshotId:     string;
  symbol:         string;
  quality:        DecisionQuality;
  /** 'early' = acted before key turning point, 'on_time' = aligned, 'late' = after */
  timing:         DecisionTiming;
  calibrationFit: 'well_calibrated' | 'overconfident' | 'underconfident' | 'unknown';
  signalClarity:  'clear' | 'mixed' | 'ambiguous';
  primaryDriver:  PrimaryAttributionLayer;
  outcomeQuality: string;
  conviction:     number;
  rMultiple:      number | null;
  explanation:    string;
  appliedRules:   string[];
}

export interface PatternInsight {
  key:           string;
  frequency:     number;
  frequencyRate: number;
  /** Structured context labels — from attribution layer + applied rules, NOT reasoning strings */
  contexts:      string[];
  winRate:       number | null;
  reliable:      boolean;
  summary:       string;
}

export interface OperatorGuidancePackage {
  trustMore:     string[];
  trustLess:     string[];
  watchFor:      string[];
  calibNotes:    string[];
  evidenceBasis: number;
}

export interface SessionIntelligenceSummary {
  symbol:             string;
  sessionScore:       SessionScore;
  decisionGrades:     DecisionGrade[];
  patternInsights:    PatternInsight[];
  guidance:           OperatorGuidancePackage;
  strongestCall:      DecisionGrade | null;
  weakestCall:        DecisionGrade | null;
  majorTurningPoint:  TurningPoint | null;
  reasoning:          string[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function _round1(v: number): number { return Math.round(v * 10) / 10; }

// ── Timing classification ─────────────────────────────────────────────────────
//
// Uses ReplayIntelligenceOutput.turningPoints (structured, deterministic).
//
// Logic:
//   If no turning points → 'unknown'
//   If snapshot occurred BEFORE the first major turning point index → 'early'
//   If snapshot occurred WITHIN TIMING_WINDOW_SNAPSHOTS of any turning point → 'on_time'
//   If snapshot occurred AFTER the last turning point → 'late'

function _classifyTiming(
  snapshotTs:     number,
  turningPoints:  TurningPoint[],
  allSnapshotTs:  number[],
): DecisionTiming {
  if (turningPoints.length === 0 || allSnapshotTs.length === 0) return 'unknown';

  const sortedTs    = [...allSnapshotTs].sort((a, b) => a - b);
  const snapIdx     = sortedTs.findIndex(ts => ts >= snapshotTs);
  const tpIndices   = turningPoints.map(tp => sortedTs.findIndex(ts => ts >= tp.ts));

  if (tpIndices.every(idx => idx === -1)) return 'unknown';

  const firstTpIdx  = Math.min(...tpIndices.filter(i => i >= 0));
  const lastTpIdx   = Math.max(...tpIndices.filter(i => i >= 0));
  const W           = T.TIMING_WINDOW_SNAPSHOTS;

  // 'on_time' — within window of ANY turning point
  const nearAnyTp = tpIndices.filter(i => i >= 0).some(
    tpIdx => Math.abs(snapIdx - tpIdx) <= W
  );
  if (nearAnyTp) return 'on_time';

  // 'early' — before the first turning point
  if (snapIdx < firstTpIdx - W) return 'early';

  // 'late' — after the last turning point
  if (snapIdx > lastTpIdx + W) return 'late';

  return 'on_time';  // in between turning points
}

// ── Session scoring ───────────────────────────────────────────────────────────

function _buildSessionScore(
  evaluations:  OutcomeValidationResult[],
  summary:      SessionAttributionSummary,
  replayOutput: ReplayIntelligenceOutput,
): SessionScore {
  const noData: SessionScore = {
    overall: 0, grade: 'insufficient_data',
    components: { winRate: null, calibration: null, conviction: null, signalClarity: null, trapQuality: null, replayQuality: null },
    reasoning: [`Insufficient data (${evaluations.length} < ${T.MIN_EVALS_FOR_SCORE} required)`],
  };

  if (evaluations.length < T.MIN_EVALS_FOR_SCORE) return noData;
  const actedOn = evaluations.filter(e => ['worked','failed','acted_on'].includes(e.decisionStatus));
  if (actedOn.length === 0) return noData;

  // Win rate
  const winRate  = summary.winRate ?? 0;
  const winScore = _round1(winRate * 100);

  // Calibration quality
  const calibCounts = { well_calibrated: 0, overconfident: 0, underconfident: 0 };
  for (const e of actedOn) {
    const r = e.calibration.calibrationResult as keyof typeof calibCounts;
    if (r in calibCounts) calibCounts[r]++;
  }
  const ct          = actedOn.length;
  const calibScore  = _round1(Math.max(0, (calibCounts.well_calibrated / ct - calibCounts.overconfident / ct * 0.5) * 100));

  // Conviction quality
  const avgConv    = actedOn.reduce((s, e) => s + e.conviction, 0) / actedOn.length;
  const convScore  = _round1(Math.min(100, Math.max(0, avgConv)));

  // Signal clarity: multi-driver fraction
  const clearCount = actedOn.filter(e => e.attribution.convergence === 'multi_driver').length;
  const clarityScore = _round1((clearCount / actedOn.length) * 100);

  // Trap quality (structured): fraction of trap-warned decisions that worked
  const trapWarned  = actedOn.filter(e => e.attribution.weights.some(w => w.layer === 'trap'));
  let trapScore: number | null = null;
  if (trapWarned.length >= T.PATTERN_MIN_OCCURRENCES) {
    const trapWins  = trapWarned.filter(e => e.outcomeQuality === 'strong_win' || e.outcomeQuality === 'weak_win');
    const trapWinRate = trapWins.length / trapWarned.length;
    // High trap win rate = operator used trap warnings well
    trapScore = _round1(trapWinRate * 100);
  }

  // Replay turning-point quality: ratio of turning points with prior adequate signal
  // Simple metric: if we have turning points and regime changes, session was complex but instrumented
  const tpCount    = replayOutput.turningPoints.length;
  const eventCount = replayOutput.events.length;
  let replayScore: number | null = null;
  if (replayOutput.snapshotCount > 0) {
    // More structured events captured = better instrumented session (cap at 100)
    const eventDensity = Math.min(1, eventCount / Math.max(1, replayOutput.snapshotCount));
    const tpCoverage   = tpCount > 0 ? Math.min(1, tpCount / 3) : 0;
    replayScore = _round1((eventDensity * 0.5 + tpCoverage * 0.5) * 100);
  }

  const overall = _round1(
    winScore      * T.SCORE_WEIGHT_WIN_RATE       +
    calibScore    * T.SCORE_WEIGHT_CALIBRATION     +
    convScore     * T.SCORE_WEIGHT_CONVICTION      +
    clarityScore  * T.SCORE_WEIGHT_SIGNAL_CLARITY  +
    (trapScore   ?? 50) * T.SCORE_WEIGHT_TRAP_QUALITY  +
    (replayScore ?? 50) * T.SCORE_WEIGHT_REPLAY_QUALITY
  );

  const grade: SessionGrade =
    overall >= T.GRADE_A_MIN ? 'A' :
    overall >= T.GRADE_B_MIN ? 'B' :
    overall >= T.GRADE_C_MIN ? 'C' :
    overall >= T.GRADE_D_MIN ? 'D' : 'F';

  return {
    overall, grade,
    components: { winRate: winScore, calibration: calibScore, conviction: convScore, signalClarity: clarityScore, trapQuality: trapScore, replayQuality: replayScore },
    reasoning: [
      `Win rate: ${(winRate * 100).toFixed(0)}% → ${winScore}`,
      `Calibration: ${(calibCounts.well_calibrated / ct * 100).toFixed(0)}% well-cal → ${calibScore}`,
      `Avg conviction: ${avgConv.toFixed(0)}% → ${convScore}`,
      `Signal clarity (multi-driver): ${clearCount}/${actedOn.length} → ${clarityScore}`,
      `Trap quality: ${trapScore !== null ? trapScore : 'N/A (insufficient data)'}`,
      `Replay quality: ${replayScore !== null ? replayScore : 'N/A'}`,
      `Overall: ${overall} → Grade ${grade}`,
    ],
  };
}

// ── Decision grading ──────────────────────────────────────────────────────────

function _gradeDecision(
  e:              OutcomeValidationResult,
  timing:         DecisionTiming,
): DecisionGrade {
  const isWin    = e.outcomeQuality === 'strong_win' || e.outcomeQuality === 'weak_win';
  const isLoss   = e.outcomeQuality === 'weak_loss'  || e.outcomeQuality === 'strong_loss';
  const isStrong = e.outcomeQuality === 'strong_win' || e.outcomeQuality === 'strong_loss';

  let quality: DecisionQuality = 'medium';
  const rules: string[] = [];

  if (isWin && e.calibration.calibrationResult === 'well_calibrated') {
    quality = 'high';  rules.push('GRADE_HIGH_WIN_CALIBRATED');
  } else if (isStrong && isWin) {
    quality = 'high';  rules.push('GRADE_HIGH_STRONG_WIN');
  } else if (isLoss && e.calibration.calibrationResult === 'overconfident') {
    quality = 'low';   rules.push('GRADE_LOW_LOSS_OVERCONFIDENT');
  } else if (isLoss && e.conviction >= T.HIGH_CONVICTION_MIN) {
    quality = 'low';   rules.push('GRADE_LOW_LOSS_HIGH_CONVICTION');
  } else if (e.outcomeQuality === 'unknown' || e.decisionStatus === 'pending') {
    quality = 'unknown'; rules.push('GRADE_UNKNOWN_NO_OUTCOME');
  } else {
    rules.push('GRADE_MEDIUM_BASELINE');
  }

  // Timing penalty: late + loss = lower grade
  if (timing === 'late' && isLoss && quality !== 'low') {
    quality = 'low';
    rules.push('GRADE_TIMING_LATE_LOSS');
  }
  // Timing bonus: on_time + high quality = keep
  if (timing === 'on_time') rules.push('GRADE_TIMING_ON_TIME');

  const signalClarity: DecisionGrade['signalClarity'] =
    e.attribution.convergence === 'multi_driver' ? 'clear' :
    e.attribution.convergence === 'single_driver' ? 'mixed' : 'ambiguous';

  const calibFit = e.calibration.calibrationResult === 'insufficient_data'
    ? 'unknown'
    : e.calibration.calibrationResult as DecisionGrade['calibrationFit'];

  const parts = [
    `${e.expectedDir.toUpperCase()} @ ${e.conviction}% conv`,
    `outcome: ${e.outcomeQuality.replace('_', ' ')}`,
    `driver: ${e.attribution.primaryDriver}`,
    `timing: ${timing}`,
    `calib: ${calibFit}`,
  ];
  if (e.rMultiple != null) parts.push(`${e.rMultiple}R`);

  return {
    snapshotId: e.snapshotId, symbol: e.symbol,
    quality, timing, calibrationFit: calibFit, signalClarity,
    primaryDriver: e.attribution.primaryDriver,
    outcomeQuality: e.outcomeQuality, conviction: e.conviction,
    rMultiple: e.rMultiple, explanation: parts.join(' | '), appliedRules: rules,
  };
}

// ── Pattern detection (structured-first) ─────────────────────────────────────
//
// Contexts derived from structured attribution fields ONLY.
// No reasoning-string parsing.

function _detectPatterns(evaluations: OutcomeValidationResult[]): PatternInsight[] {
  const patterns: PatternInsight[] = [];
  const n = evaluations.length;
  if (n === 0) return patterns;

  // Pattern 1: dominant attribution layer (structured: attribution.primaryDriver)
  const layerGroups: Record<string, OutcomeValidationResult[]> = {};
  for (const e of evaluations) {
    const k = e.attribution.primaryDriver;
    if (!layerGroups[k]) layerGroups[k] = [];
    layerGroups[k].push(e);
  }

  for (const [layer, evals] of Object.entries(layerGroups)) {
    if (evals.length < T.PATTERN_MIN_OCCURRENCES) continue;
    const actedOn = evals.filter(e => e.decisionStatus === 'worked' || e.decisionStatus === 'failed');
    const wins    = actedOn.filter(e => e.outcomeQuality === 'strong_win' || e.outcomeQuality === 'weak_win');
    const winRate = actedOn.length > 0 ? wins.length / actedOn.length : null;
    const reliable = winRate !== null && winRate >= T.HIGH_QUALITY_WIN_RATE;

    // Structured contexts: unique applied rules from these evaluations
    const ctxSet = new Set<string>();
    for (const e of evals) {
      for (const rule of e.appliedRules.filter(r => r.startsWith('ATTR_') || r.startsWith('DIR_'))) {
        ctxSet.add(rule.slice(5).toLowerCase());  // strip ATTR_/DIR_ prefix
      }
    }
    const contexts = [...ctxSet].slice(0, 3);

    patterns.push({
      key:           `layer:${layer}`,
      frequency:     evals.length,
      frequencyRate: _round1(evals.length / n),
      contexts:      contexts.length > 0 ? contexts : ['no_structured_context'],
      winRate:       winRate !== null ? _round1(winRate) : null,
      reliable,
      summary:       `${layer} primary driver: ${evals.length}/${n} snapshots${winRate !== null ? `, ${(winRate * 100).toFixed(0)}% win rate` : ''}`,
    });
  }

  // Pattern 2: overconfidence (structured: calibration.calibrationResult)
  const overconf = evaluations.filter(e => e.calibration.calibrationResult === 'overconfident');
  if (overconf.length >= T.PATTERN_MIN_OCCURRENCES) {
    const highConvOverconf = overconf.filter(e => e.conviction >= T.HIGH_CONVICTION_MIN).length;
    patterns.push({
      key:           'calibration:overconfident',
      frequency:     overconf.length,
      frequencyRate: _round1(overconf.length / n),
      contexts:      highConvOverconf > 0 ? ['high_conviction'] : [],
      winRate:       null,
      reliable:      false,
      summary:       `Overconfidence in ${overconf.length}/${n} decisions${highConvOverconf > 0 ? ` (${highConvOverconf} with conviction ≥${T.HIGH_CONVICTION_MIN}%)` : ''}`,
    });
  }

  // Pattern 3: trap signals present at loss time (structured: attribution.weights)
  const trapLosses = evaluations.filter(e =>
    e.attribution.weights.some(w => w.layer === 'trap') &&
    (e.outcomeQuality === 'weak_loss' || e.outcomeQuality === 'strong_loss')
  );
  if (trapLosses.length >= T.PATTERN_MIN_OCCURRENCES) {
    patterns.push({
      key:           'trap:conflict_with_loss',
      frequency:     trapLosses.length,
      frequencyRate: _round1(trapLosses.length / n),
      contexts:      ['trap_active'],
      winRate:       0,
      reliable:      false,
      summary:       `Trap signals present at ${trapLosses.length} losses — review trap context before acting`,
    });
  }

  // Pattern 4: late timing losses (structured: requires timing from decision grades)
  // This is built after grading — passed in as an optional enrichment later

  return patterns.sort((a, b) => b.frequency - a.frequency);
}

// ── Operator guidance ─────────────────────────────────────────────────────────

function _buildGuidance(
  evaluations:     OutcomeValidationResult[],
  summary:         SessionAttributionSummary,
  patterns:        PatternInsight[],
  learningProfile: AdaptiveLearningProfile | null,
): OperatorGuidancePackage {
  const trustMore:  string[] = [];
  const trustLess:  string[] = [];
  const watchFor:   string[] = [];
  const calibNotes: string[] = [];
  const actedOn = evaluations.filter(e => ['worked','failed','acted_on'].includes(e.decisionStatus));
  const n = actedOn.length;

  for (const p of patterns) {
    if (p.reliable && p.winRate !== null && p.winRate >= T.HIGH_QUALITY_WIN_RATE) {
      trustMore.push(`${p.key.replace('layer:', '')} signals (${(p.winRate * 100).toFixed(0)}% win rate, ${p.frequency} occurrences)`);
    }
    if (!p.reliable && p.winRate !== null && p.winRate <= T.LOW_QUALITY_WIN_RATE && p.frequency >= T.PATTERN_MIN_OCCURRENCES) {
      trustLess.push(`${p.key.replace('layer:', '')} as sole driver (${(p.winRate * 100).toFixed(0)}% win rate)`);
    }
  }

  if (patterns.find(p => p.key === 'trap:conflict_with_loss')) {
    watchFor.push('Trap signals at entry — correlated with losses in this session');
  }
  if (summary.dominantAttributionLayer === 'mixed') {
    watchFor.push('Multi-driver convergence present in best setups — wait for convergence before acting');
  }

  if (n > 0) {
    const overRate  = actedOn.filter(e => e.calibration.calibrationResult === 'overconfident').length / n;
    const underRate = actedOn.filter(e => e.calibration.calibrationResult === 'underconfident').length / n;
    if (overRate > T.OVERCONF_RATE_GUIDANCE)
      calibNotes.push(`${(overRate * 100).toFixed(0)}% overconfident — reduce conviction by ~5–10% in ambiguous setups`);
    if (underRate > T.UNDERCONF_RATE_GUIDANCE)
      calibNotes.push(`${(underRate * 100).toFixed(0)}% underconfident — system edge is stronger than conviction reflects`);
    if (overRate <= 0.25 && underRate <= 0.25 && n >= T.MIN_EVALS_FOR_SCORE)
      calibNotes.push('Calibration is broadly appropriate — conviction tracking outcomes well');
  }

  if (learningProfile?.meta.sufficientData) {
    for (const r of learningProfile.reliability) {
      if (r.reliabilityTier === 'high' && !trustMore.some(t => t.startsWith(r.signalKey)))
        trustMore.push(`${r.signalKey} (learning: ${(r.accuracyRate! * 100).toFixed(0)}% accuracy, ${r.sampleSize} samples)`);
      if (r.reliabilityTier === 'low' && !trustLess.some(t => t.startsWith(r.signalKey)))
        trustLess.push(`${r.signalKey} as primary driver (learning: ${(r.accuracyRate! * 100).toFixed(0)}% accuracy)`);
    }
  }

  if (trustMore.length === 0 && trustLess.length === 0 && watchFor.length === 0)
    watchFor.push('Mark more snapshots as worked/failed to generate specific guidance');

  return { trustMore, trustLess, watchFor, calibNotes, evidenceBasis: evaluations.length };
}

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Build a deterministic Session Intelligence Summary from existing system outputs.
 *
 * PURE FUNCTION — same inputs always produce same output.
 * No Date.now(), no I/O, no side effects.
 *
 * @param symbol          — symbol being analysed
 * @param evaluations     — from evaluateSession()
 * @param attrSummary     — from buildSessionAttributionSummary()
 * @param replayOutput    — from analyseSnapshots()
 * @param learningProfile — from buildAdaptiveLearningProfile() (optional)
 */
export function buildSessionIntelligenceSummary(
  symbol:          string,
  evaluations:     OutcomeValidationResult[],
  attrSummary:     SessionAttributionSummary,
  replayOutput:    ReplayIntelligenceOutput,
  learningProfile: AdaptiveLearningProfile | null = null,
): SessionIntelligenceSummary {
  const sessionScore    = _buildSessionScore(evaluations, attrSummary, replayOutput);

  // All snapshot timestamps for timing classification
  const allTs = evaluations.map(e => e.snapshotTs).sort((a, b) => a - b);

  // Grade each decision with timing
  const decisionGrades = evaluations.map(e => {
    const timing = _classifyTiming(e.snapshotTs, replayOutput.turningPoints, allTs);
    return _gradeDecision(e, timing);
  }).sort((a, b) => a.snapshotId.localeCompare(b.snapshotId));

  const patternInsights = _detectPatterns(evaluations);
  const guidance        = _buildGuidance(evaluations, attrSummary, patternInsights, learningProfile);

  // Best / worst calls (from graded decisions, stable sort)
  const graded = decisionGrades.filter(g => g.quality !== 'unknown');
  const qOrder: Record<DecisionQuality, number> = { high: 3, medium: 2, low: 1, unknown: 0 };
  const byQualityDesc = [...graded].sort((a, b) => qOrder[b.quality] - qOrder[a.quality]);
  const byQualityAsc  = [...graded].sort((a, b) => qOrder[a.quality] - qOrder[b.quality]);
  const strongestCall = byQualityDesc[0] ?? null;
  const weakestCall   = byQualityAsc[0]  ?? null;

  const majorTurningPoint = replayOutput.turningPoints.find(p => p.severity === 'critical')
    ?? replayOutput.turningPoints.find(p => p.severity === 'major')
    ?? null;

  const reasoning: string[] = [
    `Symbol: ${symbol} | Evaluations: ${evaluations.length} | Score: ${sessionScore.overall} (${sessionScore.grade})`,
    `Grades: ${decisionGrades.length} | Patterns: ${patternInsights.length}`,
    `Dominant driver: ${attrSummary.dominantAttributionLayer} | Calibration: ${attrSummary.overallCalibration}`,
    `Replay events: ${replayOutput.events.length} | Turning points: ${replayOutput.turningPoints.length}`,
    ...(majorTurningPoint ? [`Major turning point: ${majorTurningPoint.narrative}`] : []),
  ];

  return {
    symbol, sessionScore, decisionGrades, patternInsights, guidance,
    strongestCall, weakestCall, majorTurningPoint, reasoning,
  };
}
